// reusable customer create/edit modal — used by the customer database page and the
// Configurator's buyer information picker ("+ new customer"). on save it calls onSaved(id)
// with the created/updated customer id so the caller can link to it.

import { useState, type HTMLAttributes } from 'react';
import { CUSTOMER_TYPES, type CustomerFieldsInput } from '@technohull/shared';
import { useCreateCustomer, useUpdateCustomer } from '../features/customers.js';
import { InlineError, Modal } from './ui.js';

type Fields = CustomerFieldsInput;

const EMPTY: Fields = {
  customerType: 'individual',
  firstName: '', lastName: '', companyName: '',
  registeredAddress: '', street: '', postalCode: '', city: '', country: '',
  vatNumber: '', taxCode: '', profession: '',
  email: '', telephone: '', mobile: '', dealer: '', notes: '',
};

// Module-level (stable) field component — defining it inside the modal would remount on every
// keystroke and drop input focus. type / inputMode / autoComplete give phones the right keyboard
// (email, dial pad) and let the browser offer a saved value.
function TF({ label, value, onChange, wide, type, inputMode, autoComplete }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  wide?: boolean;
  type?: 'email' | 'tel';
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
}) {
  return (
    <label style={{ display: 'block', gridColumn: wide ? '1 / -1' : undefined }}>
      <span className="field-label">{label}</span>
      <input className="field" style={{ width: '100%' }} type={type} inputMode={inputMode} autoComplete={autoComplete}
        value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function CustomerFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial?: Partial<Fields> & { id?: number };
  onClose: () => void;
  onSaved?: (id: number) => void;
}) {
  const editId = initial?.id ?? null;
  const [f, setF] = useState<Fields>(() => ({ ...EMPTY, ...initial }) as Fields);
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const busy = create.isPending || update.isPending;
  const err = create.error || update.error;

  const set = <K extends keyof Fields>(k: K, v: Fields[K]) => setF((p) => ({ ...p, [k]: v }));
  const isCompany = f.customerType === 'company';
  const valid = !!(f.companyName.trim() || f.firstName.trim() || f.lastName.trim() || f.email.trim());

  const save = () => {
    if (!valid) return;
    if (editId) {
      update.mutate({ ...f, id: editId }, { onSuccess: () => { onSaved?.(editId); onClose(); } });
    } else {
      create.mutate(f, { onSuccess: (r) => { onSaved?.(r.id); onClose(); } });
    }
  };

  return (
    <Modal
      title={editId ? 'Edit customer' : 'New customer'}
      width={680}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!valid || busy} onClick={save}>
            {busy ? 'Saving…' : editId ? 'Save changes' : 'Create customer'}
          </button>
        </>
      }
    >
      <div role="radiogroup" aria-label="Customer type" style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
        {CUSTOMER_TYPES.map((t) => (
          <label key={t} className="check-row" style={{ fontSize: 13, textTransform: 'capitalize' }}>
            <input type="radio" name="ctype" checked={f.customerType === t} onChange={() => set('customerType', t)} /> {t}
          </label>
        ))}
      </div>

      {/* the contact fields follow the name: they are what gets filled in on the spot */}
      <div className="cust-form-grid">
        {isCompany ? (
          <TF label="Company name" value={f.companyName} onChange={(v) => set('companyName', v)} wide autoComplete="organization" />
        ) : (
          <>
            <TF label="First name" value={f.firstName} onChange={(v) => set('firstName', v)} autoComplete="given-name" />
            <TF label="Last name" value={f.lastName} onChange={(v) => set('lastName', v)} autoComplete="family-name" />
          </>
        )}
        <TF label="Email" value={f.email} onChange={(v) => set('email', v)} type="email" autoComplete="email" />
        <TF label="Telephone" value={f.telephone} onChange={(v) => set('telephone', v)} type="tel" autoComplete="tel" />
        <TF label="Mobile" value={f.mobile} onChange={(v) => set('mobile', v)} type="tel" autoComplete="tel" />
        {isCompany && (
          <>
            <TF label="Contact first name" value={f.firstName} onChange={(v) => set('firstName', v)} autoComplete="given-name" />
            <TF label="Contact last name" value={f.lastName} onChange={(v) => set('lastName', v)} autoComplete="family-name" />
          </>
        )}
        <TF label="Profession" value={f.profession} onChange={(v) => set('profession', v)} />
        <TF label="VAT number" value={f.vatNumber} onChange={(v) => set('vatNumber', v)} />
        <TF label="Tax code" value={f.taxCode} onChange={(v) => set('taxCode', v)} />
        <TF label="Dealer" value={f.dealer} onChange={(v) => set('dealer', v)} />
        <TF label="Country" value={f.country} onChange={(v) => set('country', v)} autoComplete="country-name" />
        <TF label="Registered address" value={f.registeredAddress} onChange={(v) => set('registeredAddress', v)} wide />
        <TF label="Street" value={f.street} onChange={(v) => set('street', v)} />
        {/* no numeric keypad: UK, Dutch and Canadian postcodes carry letters */}
        <TF label="Postal code" value={f.postalCode} onChange={(v) => set('postalCode', v)} autoComplete="postal-code" />
        <TF label="City" value={f.city} onChange={(v) => set('city', v)} />
        <label style={{ display: 'block', gridColumn: '1 / -1' }}>
          <span className="field-label">Notes</span>
          <textarea className="field" style={{ width: '100%' }} rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        </label>
      </div>
      {/* next to the pinned footer, so a failed save is seen where Save was pressed */}
      {err && <InlineError error={err} onDismiss={() => { create.reset(); update.reset(); }} />}
      {/* a neutral hint for why Save is disabled, not a red error before anything was typed */}
      {!valid && <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>Enter a name, company, or email.</div>}
    </Modal>
  );
}
